/**
 * One timestamp stage in the ingest pipeline.
 *
 * All four stations are rendered together, in data-flow order — a selector that
 * showed one at a time would hide exactly the comparison the page exists for.
 *
 * Each station draws its OWN scale. A broken edge clock can put one stage's p95
 * in the hundreds of seconds while the others sit in milliseconds; on a shared
 * scale that outlier flattens every other stage into a straight line and hides
 * the information the page is for. Independent scales surface the outlier
 * honestly and keep the rest readable.
 */
import { useTranslation } from "react-i18next";
import { Line, LineChart, ResponsiveContainer, YAxis } from "recharts";
import { cn } from "@/ui/lib/utils";
import { formatLatency } from "@/alerts/api/requests/alerts-monitoring.requests";
import {
  MONITORING_STAGE_LABEL_KEYS,
  type AlertMonitoringStageMetric,
  type AlertMonitoringTrendPoint,
} from "@/alerts/types/alerts-monitoring";

/**
 * Completeness wording derived from the BACKEND's missing/pending shares — never
 * from a formula invented here, and never phrased as health.
 */
const completeness = (
  stage: AlertMonitoringStageMetric,
): {
  labelKey:
    | "stageStation.completeness.notApplicable"
    | "stageStation.completeness.missing"
    | "stageStation.completeness.someGaps"
    | "stageStation.completeness.complete";
  className: string;
} => {
  if (stage.applicable_count === 0) {
    return {
      labelKey: "stageStation.completeness.notApplicable",
      className: "border-border text-muted-foreground",
    };
  }
  const missing = stage.missing_share_of_applicable ?? 0;
  const pending = stage.pending_share_of_applicable ?? 0;
  if (missing >= 0.2) {
    return {
      labelKey: "stageStation.completeness.missing",
      className: "border-destructive/40 bg-destructive/10 text-destructive",
    };
  }
  if (missing > 0 || pending > 0) {
    return {
      labelKey: "stageStation.completeness.someGaps",
      className: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
    };
  }
  return {
    labelKey: "stageStation.completeness.complete",
    className: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  };
};

/** Exact counts alongside the swatches — quality is never color-alone. */
const QUALITY = [
  { key: "valid_count", labelKey: "stageStation.quality.valid", color: "hsl(var(--chart-2))" },
  { key: "missing_count", labelKey: "stageStation.quality.missing", color: "hsl(var(--chart-5))" },
  { key: "pending_count", labelKey: "stageStation.quality.pending", color: "hsl(var(--chart-3))" },
  {
    key: "clock_skew_count",
    labelKey: "stageStation.quality.clockSkew",
    color: "hsl(var(--chart-4))",
  },
  {
    key: "not_applicable_count",
    labelKey: "stageStation.quality.notApplicable",
    color: "hsl(var(--muted-foreground))",
  },
] as const;

export const AlertStageStation = ({
  stage,
  points,
}: {
  stage: AlertMonitoringStageMetric;
  points: AlertMonitoringTrendPoint[];
}) => {
  const { t } = useTranslation("alerts");
  const status = completeness(stage);
  // A gap is a bucket with no valid samples. Dropping the nulls (rather than
  // zero-filling them) is what makes recharts break the line instead of drawing
  // a dive to zero that never happened.
  const valid = points.filter((point) => point.p95_ms != null);
  const singleBucket = valid.length === 1;

  return (
    <div className="rounded-lg border border-border p-3" tabIndex={0}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-xs font-medium">{t(MONITORING_STAGE_LABEL_KEYS[stage.code])}</h3>
        <span
          className={cn("shrink-0 rounded-full border px-1.5 py-0.5 text-[10px]", status.className)}
        >
          {t(status.labelKey)}
        </span>
      </div>

      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-lg font-semibold tabular-nums">{formatLatency(stage.p95_ms)}</span>
        <span className="text-[10px] text-muted-foreground">p95</span>
        <span className="ml-auto text-xs tabular-nums text-muted-foreground">
          p50 {formatLatency(stage.p50_ms)}
        </span>
      </div>

      <div className="mt-2 h-12">
        {singleBucket ? (
          <p className="flex h-full items-center text-[11px] text-muted-foreground">
            {t("stageStation.singleBucket")}
          </p>
        ) : valid.length === 0 ? (
          <p className="flex h-full items-center text-[11px] text-muted-foreground">
            {t("stageStation.noSamples")}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points}>
              {/* Own scale per station — see the note above. */}
              <YAxis hide domain={["dataMin", "dataMax"]} />
              <Line
                type="monotone"
                dataKey="p95_ms"
                stroke="hsl(var(--chart-1))"
                strokeWidth={2}
                dot={false}
                // Nulls stay holes in the series rather than joining across.
                connectNulls={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <dl className="mt-2 space-y-0.5">
        {QUALITY.map((quality) => {
          const count = stage[quality.key];
          if (!count) return null;
          return (
            <div key={quality.key} className="flex items-center gap-1.5 text-[11px]">
              <span
                aria-hidden
                className="inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: quality.color }}
              />
              <dt className="text-muted-foreground">{t(quality.labelKey)}</dt>
              <dd className="ml-auto tabular-nums">{count}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
};
